import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { auth, founderAdminAuth, founderAdminFunctions, normalizePhone } from '../services/firebase';
import {
  WebImpersonationSession,
  persistWebImpersonationSession,
  clearPublicImpersonationCaches,
  FounderImpersonationUserSummary,
} from '../services/webImpersonation';

type LaunchTargetType = 'find_my_clero' | 'session_gateway' | 'payer_view' | 'organizer_view';

interface SearchUserResult {
  uid: string;
  phoneNumber: string | null;
  displayName: string | null;
  username: string | null;
  email: string | null;
  exactMatchType: 'uid' | 'phone' | 'email' | null;
}

interface BootstrapResult {
  customToken: string;
  founderUser: FounderImpersonationUserSummary;
}

interface BeginResult {
  sessionId: string;
  customToken: string;
  founderUser: FounderImpersonationUserSummary;
  targetUser: SearchUserResult;
  launchPath?: string;
}

interface Props {
  founderPhone: string;
  onImpersonationStarted: (session: WebImpersonationSession) => void;
}

const FounderWebImpersonationPanel: React.FC<Props> = ({ founderPhone, onImpersonationStarted }) => {
  const [bootstrapStatus, setBootstrapStatus] = useState<'idle' | 'bootstrapping' | 'ready' | 'unsupported' | 'error'>('idle');
  const [bootstrapError, setBootstrapError] = useState('');
  const [founderUser, setFounderUser] = useState<FounderImpersonationUserSummary | null>(null);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchUserResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [launchType, setLaunchType] = useState<LaunchTargetType>('find_my_clero');
  const [launchSessionId, setLaunchSessionId] = useState('');
  const [launchInviteToken, setLaunchInviteToken] = useState('');
  const [confirmingUser, setConfirmingUser] = useState<SearchUserResult | null>(null);
  const [actionError, setActionError] = useState('');
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    const normalizedFounderPhone = normalizePhone(founderPhone);
    const currentFounderPhone = normalizePhone(founderUser?.phoneNumber || '');
    if (founderUser && normalizedFounderPhone && currentFounderPhone && normalizedFounderPhone !== currentFounderPhone) {
      setBootstrapStatus('idle');
      setBootstrapError('');
      setFounderUser(null);
      setSearchResults([]);
      setSearchError('');
      setActionError('');
    }
  }, [founderPhone, founderUser]);

  useEffect(() => {
    if (!founderPhone || !founderAdminAuth.currentUser) return;
    if (bootstrapStatus === 'ready' || bootstrapStatus === 'unsupported' || bootstrapStatus === 'bootstrapping' || bootstrapStatus === 'error') return;

    const bootstrapFounder = async () => {
      setBootstrapStatus('bootstrapping');
      setBootstrapError('');
      try {
        const bootstrap = httpsCallable<Record<string, never>, BootstrapResult>(
          founderAdminFunctions,
          'bootstrapFounderAdminSession'
        );
        const result = await bootstrap({});
        await signInWithCustomToken(founderAdminAuth, result.data.customToken);
        setFounderUser(result.data.founderUser);
        setBootstrapStatus('ready');
      } catch (error: any) {
        const code = String(error?.code || '');
        const message = String(error?.message || '').toLowerCase();
        if (
          code.includes('permission-denied')
          || code.includes('unauthenticated')
          || message.includes('founder access required')
          || message.includes('founder admin session required')
        ) {
          setBootstrapStatus('unsupported');
          return;
        }
        setBootstrapStatus('error');
        setBootstrapError(error?.message || 'Failed to initialize founder impersonation tools.');
      }
    };

    void bootstrapFounder();
  }, [bootstrapStatus, founderPhone]);

  const launchInputMeta = useMemo(() => {
    if (launchType === 'session_gateway' || launchType === 'organizer_view') {
      return {
        needsSessionId: true,
        needsInviteToken: false,
        helper: launchType === 'session_gateway'
          ? 'Session Gateway opens the regular invite access screen for this session.'
          : 'Organizer View opens the organizer page directly for the selected session.',
      };
    }
    if (launchType === 'payer_view') {
      return {
        needsSessionId: false,
        needsInviteToken: true,
        helper: 'Payer View opens a specific invite payment page and validates the invite belongs to the selected user.',
      };
    }
    return {
      needsSessionId: false,
      needsInviteToken: false,
      helper: 'Find My Clero opens the account-wide split lookup experience.',
    };
  }, [launchType]);

  const handleSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!query.trim() || bootstrapStatus !== 'ready') return;

    setSearching(true);
    setSearchError('');
    setActionError('');
    try {
      const search = httpsCallable<{ query: string }, { users: SearchUserResult[] }>(
        founderAdminFunctions,
        'searchImpersonationUsers'
      );
      const result = await search({ query: query.trim() });
      setSearchResults(result.data.users || []);
    } catch (error: any) {
      setSearchResults([]);
      setSearchError(error?.message || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleConfirmLaunch = async () => {
    if (!confirmingUser) return;

    setLaunching(true);
    setActionError('');
    try {
      const begin = httpsCallable<
        {
          targetUid: string;
          source: 'web_admin';
          launchTarget: { type: LaunchTargetType; sessionId?: string; inviteToken?: string };
        },
        BeginResult
      >(founderAdminFunctions, 'beginImpersonationSession');

      const result = await begin({
        targetUid: confirmingUser.uid,
        source: 'web_admin',
        launchTarget: {
          type: launchType,
          ...(launchInputMeta.needsSessionId ? { sessionId: launchSessionId.trim() } : {}),
          ...(launchInputMeta.needsInviteToken ? { inviteToken: launchInviteToken.trim() } : {}),
        },
      });

      if (auth.currentUser) {
        await signOut(auth);
      }
      await signInWithCustomToken(auth, result.data.customToken);

      clearPublicImpersonationCaches();
      const session: WebImpersonationSession = {
        sessionId: result.data.sessionId,
        source: 'web_admin',
        launchPath: result.data.launchPath,
        founderUser: result.data.founderUser,
        targetUser: result.data.targetUser,
        startedAt: new Date().toISOString(),
      };
      persistWebImpersonationSession(session);
      onImpersonationStarted(session);
      setConfirmingUser(null);

      const launchPath = result.data.launchPath || '/find-my-clero';
      window.history.pushState({}, '', launchPath);
      window.dispatchEvent(new Event('popstate'));
    } catch (error: any) {
      setActionError(error?.message || 'Failed to start impersonation.');
    } finally {
      setLaunching(false);
    }
  };

  if (bootstrapStatus === 'unsupported') {
    return null;
  }

  return (
    <section className="mt-6 mb-6 rounded-[32px] border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-100/70 p-6 shadow-[0_18px_60px_rgba(217,119,6,0.08)]">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-700">Founder tools</p>
          <h2 className="mt-2 text-2xl font-black text-slate-950">Public flow impersonation</h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Search for a user, choose the public experience you want to inspect, and launch a full browser session as that user. Every session is logged.
          </p>
        </div>
        <div className="rounded-full border border-amber-300 bg-white/80 px-3 py-1.5 text-xs font-semibold text-amber-800">
          Founder: {founderUser?.displayName || normalizePhone(founderPhone)}
        </div>
      </div>

      {bootstrapStatus === 'bootstrapping' ? (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
          Preparing founder-only impersonation access…
        </div>
      ) : null}

      {bootstrapStatus === 'error' ? (
        <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 flex items-center justify-between gap-3">
          <span>{bootstrapError}</span>
          <button
            type="button"
            onClick={() => setBootstrapStatus('idle')}
            className="text-xs font-semibold underline hover:text-rose-900 shrink-0"
          >
            Retry
          </button>
        </div>
      ) : null}

      {bootstrapStatus === 'idle' && founderPhone ? (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Founder session is still initializing. Refresh the page if the tools do not appear in a few seconds.
        </div>
      ) : null}

      {bootstrapStatus === 'ready' ? (
        <>
          <form onSubmit={handleSearch} className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(260px,0.9fr)]">
            <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_12px_40px_rgba(15,23,42,0.04)]">
              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Search user</span>
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Phone, uid, name, or email"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:bg-white"
                />
              </label>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="submit"
                  disabled={!query.trim() || searching}
                  className="rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {searching ? 'Searching…' : 'Search users'}
                </button>
                <p className="text-xs text-slate-500">Searches Firebase Auth plus profile search tokens.</p>
              </div>

              {searchError ? (
                <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {searchError}
                </div>
              ) : null}
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_12px_40px_rgba(15,23,42,0.04)]">
              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Launch route</span>
                <select
                  value={launchType}
                  onChange={(event) => setLaunchType(event.target.value as LaunchTargetType)}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-950 outline-none transition focus:border-slate-400 focus:bg-white"
                >
                  <option value="find_my_clero">Find My Clero</option>
                  <option value="session_gateway">Session Gateway</option>
                  <option value="payer_view">Payer View</option>
                  <option value="organizer_view">Organizer View</option>
                </select>
              </label>

              {launchInputMeta.needsSessionId ? (
                <label className="mt-4 block">
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Session ID</span>
                  <input
                    value={launchSessionId}
                    onChange={(event) => setLaunchSessionId(event.target.value)}
                    placeholder="session_123"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:bg-white"
                  />
                </label>
              ) : null}

              {launchInputMeta.needsInviteToken ? (
                <label className="mt-4 block">
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Invite token</span>
                  <input
                    value={launchInviteToken}
                    onChange={(event) => setLaunchInviteToken(event.target.value)}
                    placeholder="invite_token"
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:bg-white"
                  />
                </label>
              ) : null}

              <p className="mt-4 text-sm leading-6 text-slate-500">{launchInputMeta.helper}</p>
            </div>
          </form>

          {actionError ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {actionError}
            </div>
          ) : null}

          <div className="mt-5 rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_12px_40px_rgba(15,23,42,0.04)]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-slate-950">Results</p>
                <p className="mt-1 text-sm text-slate-500">Choose the user you want to impersonate in the selected public flow.</p>
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                {searchResults.length} results
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {searchResults.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
                  {query.trim() ? 'No matching users found.' : 'Search for a user to begin.'}
                </div>
              ) : (
                searchResults.map((user) => {
                  const userLabel = user.displayName || user.phoneNumber || user.email || user.uid;
                  const launchBlocked =
                    (launchInputMeta.needsSessionId && !launchSessionId.trim())
                    || (launchInputMeta.needsInviteToken && !launchInviteToken.trim());
                  return (
                    <div key={user.uid} className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 xl:flex-row xl:items-center xl:justify-between">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-950">{userLabel}</p>
                        <p className="mt-1 truncate text-sm text-slate-500">
                          {user.phoneNumber || 'No phone'}{user.email ? ` • ${user.email}` : ''}{user.username ? ` • @${user.username}` : ''}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          UID: {user.uid}
                          {user.exactMatchType ? ` • Exact ${user.exactMatchType} match` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setConfirmingUser(user)}
                        disabled={launchBlocked}
                        className="inline-flex shrink-0 items-center justify-center rounded-2xl bg-amber-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-slate-300"
                      >
                        Impersonate user
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      ) : null}

      {confirmingUser ? (
        <div className="fixed inset-0 z-[75] bg-slate-950/40 backdrop-blur-sm" onClick={() => setConfirmingUser(null)}>
          <div className="flex min-h-screen items-center justify-center px-4 py-8">
            <div
              className="w-full max-w-lg rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_28px_90px_rgba(15,23,42,0.24)]"
              onClick={(event) => event.stopPropagation()}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.26em] text-amber-700">Confirm founder impersonation</p>
              <h3 className="mt-3 text-2xl font-black text-slate-950">Start full-access founder session?</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">
                You are about to impersonate this user in a full-access founder session.
              </p>

              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                <p><span className="font-semibold text-slate-900">User:</span> {confirmingUser.displayName || confirmingUser.phoneNumber || confirmingUser.email || confirmingUser.uid}</p>
                <p className="mt-2"><span className="font-semibold text-slate-900">Route:</span> {launchType.replace(/_/g, ' ')}</p>
                {launchInputMeta.needsSessionId ? <p className="mt-2"><span className="font-semibold text-slate-900">Session:</span> {launchSessionId.trim()}</p> : null}
                {launchInputMeta.needsInviteToken ? <p className="mt-2"><span className="font-semibold text-slate-900">Invite token:</span> {launchInviteToken.trim()}</p> : null}
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    void handleConfirmLaunch();
                  }}
                  disabled={launching}
                  className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {launching ? 'Launching…' : 'Start impersonation'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingUser(null)}
                  disabled={launching}
                  className="rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default FounderWebImpersonationPanel;
